import WrapperService from "@/modules/core/services/wrapper-service";

const API_URL = process.env.VUE_APP_ROOT_API + "/api/v1/duplicate-check"
const serviceName = "Duplicate-Check-Service: ";

export default class DuplicateCheckService {

  /**
   * Reads possible duplicated buildings by specific field.
   *
   * @param field to check by (NAME, OBJECT_NUMBER, ADDRESS)
   * @param term term to check by
   * @param successF function to be called on success
   * @param errorF function to be called on failure
   * @returns {Promise<void>}
   */
  static async checkBuildings(field, term, successF, errorF) {
    await WrapperService.getWithErrorCause(API_URL + "/building/" + field + "?search=" + encodeURI(term), null, serviceName, successF, errorF);
  }

  static async readBuildingMergePreview(primaryBuildingId, buildingIds, successF, errorF) {
    let formData = new FormData();
    let paramList = {}
    paramList.list = buildingIds;
    formData.append("buildingIds", paramList)

    await WrapperService.put(API_URL + "/building/preview/" + primaryBuildingId, paramList, serviceName, successF, errorF);
  }

  /**
   * Sends request to merge passed buildings into one main building
   *
   * @param primaryBuildingId id of main building
   * @param buildingIds ids of buildings, which should be merged into the main one
   * @param successF function to be called on success
   * @param errorF function to be called on failure
   * @returns {Promise<void>}
   */
  static async mergeBuildings(primaryBuildingId, buildingIds, successF, errorF) {
    let paramList = {};
    paramList.list = buildingIds;

    await WrapperService.put(API_URL + "/building/merge/" + primaryBuildingId, paramList, serviceName, successF, errorF);
  }

  /**
   * Reads possible duplicated buildings as groups.
   *
   * @param successF function to be called on success
   * @param errorF function to be called on failure
   * @returns {Promise<void>}
   */
  static async readAllDuplicatedBuildingsFromAPI(successF, errorF) {
    await WrapperService.get(API_URL + "/building", null, serviceName, successF, errorF);
  }

  /**
   * Reads possible duplicated facilities by specific field.
   *
   * @param field to check by (NAME, FACILITY_NUMBER)
   * @param term term to check for
   * @param successF function to be called on success
   * @param errorF function to be called on failure
   * @returns {Promise<void>}
   */
  static async checkFacilities(field, term, successF, errorF) {
    await WrapperService.getWithErrorCause(API_URL + "/facility/" + field + "?search=" + encodeURI(term), null, serviceName, successF, errorF);
  }

  static async readFacilityMergePreview(primaryFacilityId, facilityIds, successF, errorF) {
    let paramList = {}
    paramList.list = facilityIds;

    await WrapperService.put(API_URL + "/facility/preview/" + primaryFacilityId, paramList, serviceName, successF, errorF);
  }

  /**
   * Sends request to merge passed facilities into one main facility
   *
   * @param primaryFacilityId id of main facility
   * @param facilityIds ids of facilities, which should be merged into the main one
   * @param successF function to be called on success
   * @param errorF function to be called on failure
   * @returns {Promise<void>}
   */
  static async mergeFacilities(primaryFacilityId, facilityIds, successF, errorF) {
    let paramList = {};
    paramList.list = facilityIds;

    await WrapperService.put(API_URL + "/facility/merge/" + primaryFacilityId, paramList, serviceName, successF, errorF);
  }

  /**
   * Reads possible duplicated facilities as groups.
   *
   * @param successF function to be called on success
   * @param errorF function to be called on failure
   * @returns {Promise<void>}
   */
  static async readAllDuplicatedFacilitiesFromAPI(successF, errorF) {
    await WrapperService.get(API_URL + "/facility", null, serviceName, successF, errorF);
  }

  /**
   * Reads possible duplicated customers by specific field.
   *
   * @param field to check by (NAME, ADDRESS)
   * @param term term to check for
   * @param successF function to be called on success
   * @param errorF function to be called on failure
   * @returns {Promise<void>}
   */
  static async checkCustomers(field, term, successF, errorF) {
    await WrapperService.get(API_URL + "/customer/" + field + "?search=" + encodeURI(term), null, serviceName, successF, errorF);
  }

  /**
   * Reads possible duplicated customers as groups.
   *
   * @param successF function to be called on success
   * @param errorF function to be called on failure
   * @returns {Promise<void>}
   */
  static async readAllDuplicatedCustomersFromAPI(successF, errorF) {
    await WrapperService.get(API_URL + "/customer", null, serviceName, successF, errorF);
  }

  /**
   * Reads the merge preview of passed primary customer and secondary customer ids.
   *
   * @param primaryCustomerId main customer id into which the other customers should be merged
   * @param customerIds secondary customer ids, which should be merged into the main customer
   * @param successF function to be called on success
   * @param errorF function to be called on failure
   * @returns {Promise<void>}
   */
  static async readCustomerMergePreview(primaryCustomerId, customerIds, successF, errorF) {
    let paramList = {}
    paramList.list = customerIds;

    await WrapperService.put(API_URL + "/customer/preview/" + primaryCustomerId, paramList, serviceName, successF, errorF);
  }

  /**
   * Sends request to merge passed customers into one main customer
   *
   * @param primaryCustomerId id of main customer
   * @param customerIds ids of customers, which should be merged into the main one
   * @param successF function to be called on success
   * @param errorF function to be called on failure
   * @returns {Promise<void>}
   */
  static async mergeCustomers(primaryCustomerId, customerIds, successF, errorF) {
    let paramList = {};
    paramList.list = customerIds;

    await WrapperService.put(API_URL + "/customer/merge/" + primaryCustomerId, paramList, serviceName, successF, errorF);
  }

  /**
   * Reads possible duplicated users as groups.
   *
   * @param successF function to be called on success
   * @param errorF function to be called on failure
   * @returns {Promise<void>}
   */
  static async readAllDuplicatedUsersFromAPI(successF, errorF) {
    await WrapperService.get(API_URL + "/users", null, serviceName, successF, errorF);
  }

  /**
   * Reads possible duplicated users by specific field.
   *
   * @param field to check by (NAME, EMAIL)
   * @param term term to check for
   * @param successF function to be called on success
   * @param errorF function to be called on failure
   * @returns {Promise<void>}
   */
  static async checkUsers(field, term, successF, errorF) {
    await WrapperService.getWithErrorCause(API_URL + "/users/" + field + "?search=" + encodeURI(term), null, serviceName, successF, errorF);
  }

  /**
   * Reads the merge preview of passed primary user and secondary user ids.
   *
   * @param primaryUserId main user id into which the other users should be merged
   * @param userIds secondary user ids, which should be merged into the main user
   * @param successF function to be called on success
   * @param errorF function to be called on failure
   * @returns {Promise<void>}
   */
  static async readUsersMergePreview(primaryUserId, userIds, successF, errorF) {
    let paramList = {}
    paramList.list = userIds;

    await WrapperService.put(API_URL + "/users/preview/" + primaryUserId, paramList, serviceName, successF, errorF);
  }

  /**
   * Sends request to merge passed users into one main user
   *
   * @param primaryUserId id of main facility
   * @param userIds ids of users, which should be merged into the main one
   * @param successF function to be called on success
   * @param errorF function to be called on failure
   * @returns {Promise<void>}
   */
  static async mergeUsers(primaryUserId, userIds, successF, errorF) {
    let paramList = {};
    paramList.list = userIds;

    await WrapperService.put(API_URL + "/users/merge/" + primaryUserId, paramList, serviceName, successF, errorF);
  }
}